import { Modal, Button, Group, Stack, NumberInput, Grid } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useEffect, useState } from 'react'
import { editValue } from 'app/state/ducks/traded-facilities/thunks'
import { loadFMMetadataForSubTrade } from 'app/state/ducks/signable-document/thunks'
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification'
import { useDispatch } from 'react-redux'
import { ReactComponent as CloseIcon } from 'app/public/DrawerIcons/close-icon.svg'
import { useTradeRefresh } from 'app/views/pages/trade-entry/detail-trade/use-trade-refresh'

interface Props {
    row: any
    opened: boolean
    close: () => void
}

function EditValueModal({ opened, close, row }: Props) {
    const dispatch = useDispatch()
    const { refreshTrade } = useTradeRefresh()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const form = useForm({
        initialValues: {
            newAmount: '' as unknown as number,
        },
        validate: {
            newAmount: (value) =>
                value === null || value === undefined || (value as any) === ''
                    ? 'Amount is required'
                    : value <= 0
                        ? 'Amount must be greater than 0'
                        : null,
        },
        validateInputOnChange: true,
        validateInputOnBlur: true,
    })

    useEffect(() => {
        if (opened) {
            const raw = typeof row?.updatedValue === 'string'
                ? parseFloat(row.updatedValue.replace(/,/g, ''))
                : row?.updatedValue
            form.setFieldValue('newAmount', isNaN(raw) ? ('' as unknown as number) : raw)
        }
    }, [opened, row?.updatedValue])

    const handleClose = () => {
        form.reset()
        close()
    }

    const handleSubmit = async (values: typeof form.values) => {
        setIsSubmitting(true)
        const body = {
            masterTradeId: row.masterTradeId,
            subTradeId: row.subTradeId,
            facilityId: row.facilityId,
            effectiveDate: row.date,
            positionAtDate: row.positionAtDate ?? 0,
            newAmount: String(values.newAmount),
            transaction:
                row.type === 'Paydown'
                    ? 'PaydownTradeTransaction'
                    : 'PIKTradeTransaction',
        }

        const result: any = await editValue(body)
        if (result?.success) {
            SuccessNotification({ title: 'Trade amount updated successfully' })
            // FM metadata is the source of truth for the row; refresh it before
            // closing so the row renders the new value instead of flashing the
            // stale one. refreshTrade() re-reads this trade's records, traded
            // facilities included (ADR_LOS-030).
            const subTradeId = row?.subTradeId?.id
            if (subTradeId) {
                await dispatch(loadFMMetadataForSubTrade(subTradeId) as any)
            }
            await refreshTrade()
            handleClose()
        } else {
            setIsSubmitting(false)
            ErrorNotification({
                title: 'Edit Value Failed',
                message: result?.payload ?? 'Failed to update trade amount',
            })
        }
    }

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            centered
            withCloseButton={false}
            size="40%"
            className="modal-body create-new-form fit-content-modal"
        >
            <Stack w="100% !important">
                <div className="add-modal-fee" style={{ width: '100%' }}>
                    <div className="add-modal-fee-title-header">
                        <div className="add-modal-fee-title">
                            <div className="add-modal-fee-title-frame">
                                <div className="add-modal-fee-title-text">
                                    Edit Update Value  — {row?.type ?? ''} ({row?.date ?? ''})
                                </div>
                            </div>
                        </div>
                        <div className="add-modal-fee-header-actions">
                            <div className="add-modal-fee-title-actionfs-expand">
                                <Button
                                    onClick={handleClose}
                                    variant="subtle"
                                    style={{
                                        padding: 0,
                                        background: 'transparent',
                                        height: 30,
                                        width: 30,
                                        minWidth: 'auto',
                                    }}
                                    aria-label="Close modal"
                                >
                                    <CloseIcon style={{ width: '30px' }} />
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="content" style={{ width: '100%' }}>
                        <form onSubmit={form.onSubmit(handleSubmit)}>
                            <div className="create-new">
                                <Grid style={{ minHeight: 'fit-content', marginTop: '10px' }} gutter="md">
                                    <Grid.Col span={12}>
                                        <NumberInput
                                            placeholder="Enter new trade amount"
                                            precision={2}
                                            parser={value =>
                                                value ? value.replace(/\$\s?|(,*)/g, '') : ''
                                            }
                                            formatter={value =>
                                                !Number.isNaN(parseFloat(value ?? ''))
                                                    ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                                    : ''
                                            }
                                            {...form.getInputProps('newAmount')}
                                        />
                                    </Grid.Col>
                                </Grid>
                                <Group position="right" mt="md">
                                    <Button
                                        className="add-modal-fee-cancel-button"
                                        onClick={handleClose}
                                    >
                                        <div className="add-modal-fee-cancel-button-text">Cancel</div>
                                    </Button>
                                    <Button
                                        disabled={!form.isValid() || isSubmitting}
                                        className="add-modal-fee-save-button"
                                        type="submit"
                                    >
                                        <div className="add-modal-fee-save-button-text">Save</div>
                                    </Button>
                                </Group>
                            </div>
                        </form>
                    </div>
                </div>
            </Stack>
        </Modal>
    )
}

export default EditValueModal
